import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Activity, ArrowLeft, List, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZOOMS = ["executive", "division", "work", "event", "evidence", "solver", "economics"];

// Parse an ISO timestamp to epoch ms (fail-safe to null).
const ms = (t) => {
  if (!t) return null;
  const v = Date.parse(t);
  return Number.isNaN(v) ? null : v;
};
const fmtMs = (n) => (n == null ? "—" : `${n} ms`);

// Semantic-zoom viewer — animates ONLY from persisted canonical Run Events and
// reopens the completed Run after refresh (run_id in the URL). Provides an accessible
// list alternative and surfaces the full governed evidence depth: queue/blocked time,
// critical path, retries/handoffs/failures, per-dispatch provider evidence (with the
// provider usage source), canonical Evaluations, solver + variable + matched-economics
// evidence, registry exclusions and governed downloads.
export default function OptimizationRunViewer() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [zoom, setZoom] = useState("division");
  const [listMode, setListMode] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workspace/optimization/runs/${runId}/timeline`);
      setData(data);
    } catch { setData({ error: true }); }
    try {
      const { data: rep } = await axios.get(`${API}/workspace/optimization/runs/${runId}/report`);
      setReport(rep.report || null);
    } catch { setReport(null); }
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const s = (data && data.run && data.run.summary) || {};
  const events = useMemo(() => (data && data.events) || [], [data]);
  const wus = useMemo(() => (data && data.work_units) || [], [data]);
  const evidence = (data && data.evidence) || [];
  const evaluations = (data && data.evaluations) || [];
  const artifacts = (data && data.artifacts) || [];
  const routing = useMemo(() => (data && data.run && data.run.provider_routing) || {}, [data]);
  const contractRes = (data && data.run && data.run.contract && data.run.contract.resolution) || {};

  // Derived timing metrics from persisted Events + Work-Unit timing.
  const metrics = useMemo(() => {
    const evByWuType = {};
    events.forEach((e) => {
      const k = `${e.work_unit_id}|${e.event_type}`;
      if (!(k in evByWuType)) evByWuType[k] = ms(e.timestamp);
    });
    let queueTotal = 0, queueCount = 0;
    const durations = {};
    wus.forEach((w) => {
      const sched = evByWuType[`${w.work_unit_id}|work.scheduled`];
      const start = ms(w.timing && w.timing.started_at) || evByWuType[`${w.work_unit_id}|work.started`];
      const fin = ms(w.timing && w.timing.finished_at);
      if (sched != null && start != null && start >= sched) { queueTotal += start - sched; queueCount += 1; }
      durations[w.work_unit_id] = (start != null && fin != null && fin >= start) ? fin - start : 0;
    });
    // Critical path: longest ELAPSED path through the dependency DAG using persisted
    // event timings (each node contributes its own elapsed duration; the path is the
    // longest chain of dependency edges).
    const byId = {}; wus.forEach((w) => { byId[w.work_unit_id] = w; });
    const memo = {};
    const longest = (id, seen) => {
      if (memo[id] != null) return memo[id];
      if (seen.has(id)) return 0;
      seen.add(id);
      const deps = (byId[id] && byId[id].dependencies) || [];
      let best = 0;
      deps.forEach((d) => { best = Math.max(best, longest(d, new Set(seen))); });
      memo[id] = best + (durations[id] || 0);
      return memo[id];
    };
    let critical = 0;
    wus.forEach((w) => { critical = Math.max(critical, longest(w.work_unit_id, new Set())); });
    // A handoff is a dependency EDGE crossed — count unlocked events only for units that
    // actually have >=1 dependency (root units are not handoffs).
    const handoffs = events.filter((e) => e.event_type === "dependency.unlocked"
      && ((e.metadata && (e.metadata.dependencies || []).length) > 0)).length;
    const failures = events.filter((e) => /fail|incomplete|error/i.test(e.event_type)).length;
    const per = routing.per_dispatch || [];
    // Retries: prefer the authoritative aggregate total; fall back to per-dispatch sum.
    // NEVER add both (that double-counts the same retries).
    const retries = (routing.aggregate && typeof routing.aggregate.retries_total === "number")
      ? routing.aggregate.retries_total
      : per.reduce((a, d) => a + (d.retries || 0), 0);
    // Blocked time: from each unit's last dependency completion to its own start.
    let blockedTotal = 0;
    wus.forEach((w) => {
      const start = ms(w.timing && w.timing.started_at)
        || evByWuType[`${w.work_unit_id}|work.started`];
      const deps = w.dependencies || [];
      if (start == null || deps.length === 0) return;
      let depDone = null;
      deps.forEach((d) => {
        const dw = byId[d];
        const df = dw && ms(dw.timing && dw.timing.finished_at);
        if (df != null) depDone = depDone == null ? df : Math.max(depDone, df);
      });
      if (depDone != null && start >= depDone) blockedTotal += start - depDone;
    });
    return {
      queueAvg: queueCount ? Math.round(queueTotal / queueCount) : null,
      queueTotal, critical, handoffs, failures, retries, blockedTotal,
    };
  }, [events, wus, routing]);

  if (!data) return <div className="p-8 text-[var(--qn-foreground-muted)]" role="status" aria-live="polite" data-testid="opt-run-loading">Loading run…</div>;
  if (data.error) return <div className="p-8 text-[var(--qn-foreground-muted)]" role="alert" data-testid="opt-run-error">Run not found.</div>;

  const completed = wus.filter((w) => w.status === "completed").length;
  const peak = s.peak_concurrency || 0;
  const fmtTs = (t) => (t ? String(t).slice(11, 23) : "");
  const registryExclusions = s.registry_exclusions || contractRes.registry_exclusions || [];
  const per = routing.per_dispatch || [];
  const solver = report && report.sections && report.sections.solver;
  const variables = report && report.sections && report.sections.variables;
  const economics = report && report.sections && report.sections.economics;
  const limitations = (report && report.limitations) || [];
  const comparison = data && data.run && data.run.comparison;
  const matchedLink = data && data.run && data.run.matched_link;

  const onZoomKey = (e, i) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = e.key === "ArrowRight" ? (i + 1) % ZOOMS.length : (i - 1 + ZOOMS.length) % ZOOMS.length;
      setZoom(ZOOMS[next]);
      const el = document.getElementById(`opt-zoom-${ZOOMS[next]}`);
      if (el) el.focus();
    }
  };

  const Metric = ({ k, v, tid }) => (
    <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3" data-testid={tid}>
      <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)]">{k}</div>
      <div className="text-lg font-semibold mt-0.5 text-[var(--qn-foreground)]">{v}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--qn-background)] text-[var(--qn-foreground)] motion-reduce:transition-none" data-testid="opt-run-viewer">
      <div className="flex items-center justify-between px-8 pt-8 pb-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/app/optimization")} className="gap-1.5" aria-label="Back to optimization"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5 text-[var(--qn-primary)]" /> Optimization Run</h1>
            <div className="text-xs font-mono text-[var(--qn-foreground-subtle)]">{data.run.run_id} · {data.run.status}{s.installation_id ? ` · install ${s.installation_id}` : ""}</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setListMode(!listMode)} data-testid="opt-toggle-list" className="gap-1.5" aria-pressed={listMode}>
          {listMode ? <LayoutGrid className="w-3.5 h-3.5" /> : <List className="w-3.5 h-3.5" />}{listMode ? "Zoom view" : "List view"}
        </Button>
      </div>

      <div className="px-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
        <Metric k="Selected divisions" v={(s.selected_divisions || []).length} tid="opt-metric-selected-divisions" />
        <Metric k="Runtime work units" v={s.runtime_work_units || wus.length} tid="opt-metric-runtime-work-units" />
        <Metric k="Completed" v={`${completed}/${wus.length}`} tid="opt-metric-completed" />
        <Metric k="Peak concurrency" v={peak} tid="opt-metric-peak-concurrency" />
        <Metric k="Avg queue time" v={fmtMs(metrics.queueAvg)} tid="opt-metric-queue-time" />
        <Metric k="Critical path" v={fmtMs(metrics.critical)} tid="opt-metric-critical-path" />
        <Metric k="Blocked time" v={fmtMs(metrics.blockedTotal)} tid="opt-metric-blocked-time" />
        <Metric k="Handoffs" v={metrics.handoffs} tid="opt-metric-handoffs" />
        <Metric k="Retries" v={metrics.retries} tid="opt-metric-retries" />
        <Metric k="Failures" v={metrics.failures} tid="opt-metric-failures" />
        <Metric k="Model dispatches" v={s.model_dispatches || per.length} tid="opt-metric-dispatches" />
      </div>

      {!listMode && (
        <div className="px-8 pb-2 flex flex-wrap gap-2" role="tablist" aria-label="Zoom level" data-testid="opt-zoom-controls">
          {ZOOMS.map((z, i) => (
            <button key={z} id={`opt-zoom-${z}`} data-testid={`opt-zoom-${z}`} role="tab" aria-selected={zoom === z}
              tabIndex={zoom === z ? 0 : -1} onKeyDown={(e) => onZoomKey(e, i)} onClick={() => setZoom(z)}
              className={`px-3 py-1.5 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)] ${zoom === z ? "bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]" : "bg-[var(--qn-card)] border border-[var(--qn-border)] text-[var(--qn-foreground-muted)]"}`}>{z}</button>
          ))}
        </div>
      )}

      <div className="px-8 pb-12">
        {listMode ? (
          <div className="space-y-1" data-testid="opt-event-list" role="list">
            {events.map((e) => (
              <div key={e.sequence} role="listitem" className="text-xs font-mono flex gap-3 border-b border-[var(--qn-border)] py-1" data-testid={`opt-event-row-${e.sequence}`}>
                <span className="text-[var(--qn-foreground-subtle)]">#{e.sequence}</span>
                <span className="text-[var(--qn-foreground-subtle)] w-24">{fmtTs(e.timestamp)}</span>
                <span className="text-[var(--qn-primary)] w-56">{e.event_type}</span>
                <span className="text-[var(--qn-foreground-muted)]">{e.work_unit_id || ""}</span>
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="opt-zoom-canvas" role="tabpanel" aria-label={`${zoom} view`}>
            {zoom === "executive" && (
              <div className="flex flex-wrap gap-2">{(s.executive_divisions || []).map((d, i) => (
                <span key={i} data-testid={`opt-exec-division-${i}`} data-selected={(s.selected_divisions || []).includes(d) ? "true" : "false"} className={`px-3 py-2 rounded-lg text-sm border ${(s.selected_divisions || []).includes(d) ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]" : "border-[var(--qn-border)] bg-[var(--qn-card)] text-[var(--qn-foreground-subtle)]"}`}>{String(d)}</span>
              ))}</div>
            )}
            {zoom === "division" && (
              <>
                <div className="flex flex-wrap gap-2">{(s.selected_divisions || []).map((d, i) => (
                  <span key={i} data-testid={`opt-division-${i}`} className="px-3 py-2 rounded-lg text-sm border border-[var(--qn-primary)] bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]">{String(d)}</span>
                ))}</div>
                {registryExclusions.length > 0 && (
                  <div className="mt-4 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3" data-testid="opt-registry-exclusions">
                    <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Registry exclusions ({registryExclusions.length})</div>
                    {registryExclusions.map((x, i) => (
                      <div key={i} className="text-xs font-mono text-amber-500">{x.kind || "item"} · {x.id || ""} · {x.reason}</div>
                    ))}
                  </div>
                )}
                {(s.excluded || []).length > 0 && (
                  <div className="mt-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3" data-testid="opt-graph-exclusions">
                    <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Compiler exclusions ({s.excluded.length})</div>
                    {s.excluded.map((x, i) => (<div key={i} className="text-xs font-mono text-[var(--qn-foreground-muted)]">{x.division || ""} {x.capability || ""} · {x.reason}</div>))}
                  </div>
                )}
              </>
            )}
            {zoom === "work" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">{wus.map((w) => (
                <div key={w.work_unit_id} data-testid={`opt-wu-${w.work_unit_id}`} className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3">
                  <div className="text-xs font-medium">{w.capability}</div>
                  <div className="text-[11px] text-[var(--qn-foreground-subtle)] font-mono">{w.work_unit_id}</div>
                  <div className="text-[11px] mt-1"><span className={w.status === "completed" ? "text-emerald-500" : "text-amber-500"}>{w.status}</span> · {w.verification_state || "—"} · <span className="text-[var(--qn-foreground-subtle)]">{w.implementation}</span></div>
                  {(w.dependencies || []).length > 0 && (
                    <div className="text-[10px] mt-1 text-[var(--qn-foreground-subtle)] font-mono">deps: {w.dependencies.join(", ")}</div>
                  )}
                  {w.timing && w.timing.started_at && (
                    <div className="text-[10px] mt-0.5 text-[var(--qn-foreground-subtle)] font-mono">{fmtTs(w.timing.started_at)} → {fmtTs(w.timing.finished_at)}</div>
                  )}
                </div>
              ))}</div>
            )}
            {zoom === "event" && (
              <div className="space-y-1" data-testid="opt-event-canvas-list" role="list">{events.map((e) => (
                <div key={e.sequence} role="listitem" data-testid={`opt-event-row-${e.sequence}`} className="text-xs font-mono flex gap-3"><span className="text-[var(--qn-foreground-subtle)]">#{e.sequence}</span><span className="text-[var(--qn-foreground-subtle)] w-24">{fmtTs(e.timestamp)}</span><span className="text-[var(--qn-primary)]">{e.event_type}</span><span className="text-[var(--qn-foreground-muted)]">{e.work_unit_id || ""}</span></div>
              ))}</div>
            )}
            {zoom === "evidence" && (
              <div className="space-y-4" data-testid="opt-evidence-panel">
                <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3" data-testid="opt-provider-routing">
                  <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Provider routing</div>
                  {(routing.selected && routing.selected.provider) || per.length > 0 ? (
                    <div className="text-xs">Dispatched <span className="text-[var(--qn-primary)]">{routing.selected.provider}/{routing.selected.model}</span>{routing.aggregate ? ` · ${routing.aggregate.dispatch_count} dispatches` : ""} · usage {(routing.aggregate || routing.dispatch || {}).usage_source || "unavailable"} · quality {(routing.dispatch || {}).quality_verified ? "verified" : "not verified"}</div>
                  ) : (
                    <div className="text-xs text-amber-500">Provider routing unavailable: {routing.unavailable_reason || "no_model_dispatch"} (no attributable provider response)</div>
                  )}
                  {per.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="opt-per-dispatch">
                      {per.map((d, i) => (
                        <div key={i} data-testid={`opt-dispatch-${i}`} className="text-[11px] font-mono flex gap-3 border-t border-[var(--qn-border)] pt-1">
                          <span className="text-[var(--qn-primary)] w-40">{d.provider}/{d.model}</span>
                          <span className="text-[var(--qn-foreground-muted)]">in {d.tokens_in == null ? "n/a" : d.tokens_in} · out {d.tokens_out == null ? "n/a" : d.tokens_out}</span>
                          <span className="text-[var(--qn-foreground-subtle)]">src {d.usage_source}</span>
                          <span className={d.quality_verified ? "text-emerald-500" : "text-amber-500"}>{d.quality_verified ? "quality✓" : "quality✗"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3" data-testid="opt-evaluations">
                  <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Quality evaluations ({evaluations.length})</div>
                  {evaluations.map((ev) => (
                    <div key={ev.evaluation_id} data-testid={`opt-eval-${ev.evaluation_id}`} className="text-xs font-mono flex gap-3 border-b border-[var(--qn-border)] py-1">
                      <span className={ev.verdict === "pass" ? "text-emerald-500 w-14" : "text-amber-500 w-14"}>{ev.verdict}</span>
                      <span className="text-[var(--qn-foreground-muted)] truncate">{ev.criterion}</span>
                    </div>
                  ))}
                  {evaluations.length === 0 && <div className="text-xs text-[var(--qn-foreground-subtle)]">No governed evaluation recorded.</div>}
                </div>
                <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3">
                  <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Evidence ({evidence.length})</div>
                  {evidence.map((ev) => (
                    <div key={ev.evidence_id} data-testid={`opt-evidence-${ev.evidence_id}`} className="text-xs font-mono flex gap-3 border-b border-[var(--qn-border)] py-1">
                      <span className={ev.status === "verified" ? "text-emerald-500 w-28" : "text-amber-500 w-28"}>{ev.status}</span>
                      <span className="text-[var(--qn-foreground-muted)] w-40">{ev.producing_activity}</span>
                      <span className="text-[var(--qn-foreground-subtle)]">{ev.source_entity}</span>
                    </div>
                  ))}
                  {evidence.length === 0 && <div className="text-xs text-[var(--qn-foreground-subtle)]">No evidence recorded.</div>}
                </div>
                <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3">
                  <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1 flex items-center justify-between">
                    <span>Governed artifacts ({artifacts.length})</span>
                    <a href={`${API}/workspace/optimization/runs/${runId}/report`} target="_blank" rel="noreferrer"
                       data-testid="opt-download-report" className="text-[var(--qn-primary)] underline focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]">Download report</a>
                  </div>
                  {artifacts.map((a) => (
                    <div key={a.artifact_id} data-testid={`opt-artifact-${a.artifact_id}`} className="text-xs font-mono flex gap-3 py-1">
                      <span className="text-[var(--qn-primary)] w-28">{a.kind}</span>
                      <span className="text-[var(--qn-foreground-muted)] w-48">{a.report_key || "—"} v{a.version}</span>
                      <span className="text-[var(--qn-foreground-subtle)] truncate">{(a.digest || "").slice(0, 16)}</span>
                    </div>
                  ))}
                  {artifacts.length === 0 && <div className="text-xs text-[var(--qn-foreground-subtle)]">No governed report artifact.</div>}
                </div>
              </div>
            )}
            {zoom === "solver" && (
              <div className="space-y-3" data-testid="opt-solver-panel">
                <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-4">
                  <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Solver selection</div>
                  {solver ? (
                    <>
                      <div className="text-sm"><span className={solver.label === "quantum_selected" ? "text-[var(--qn-primary)]" : "text-[var(--qn-foreground)]"}>{solver.label}</span></div>
                      <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">engine: {solver.provenance && solver.provenance.engine} · quantum dispatched: {String(solver.provenance && solver.provenance.quantum_dispatched)}</div>
                      <div className="text-xs text-amber-500 mt-1" data-testid="opt-solver-reason">reason: {solver.provenance && solver.provenance.reason}</div>
                      {solver.provenance && solver.provenance.problem_digest && (
                        <div className="text-[10px] font-mono text-[var(--qn-foreground-subtle)] mt-1">problem {String(solver.provenance.problem_digest).slice(0, 16)} · response {String(solver.provenance.response_digest || "").slice(0, 16)}</div>
                      )}
                    </>
                  ) : <div className="text-xs text-[var(--qn-foreground-subtle)]">Solver evidence unavailable (no report).</div>}
                </div>
                <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-4" data-testid="opt-variables-panel">
                  <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Hidden variables</div>
                  {variables ? (
                    <div className="text-xs">retained: <span className="text-emerald-500">{variables.kept_count}</span> · pruned: <span className="text-amber-500">{variables.pruned_count}</span></div>
                  ) : <div className="text-xs text-[var(--qn-foreground-subtle)]">Variable evidence unavailable.</div>}
                </div>
              </div>
            )}
            {zoom === "economics" && (
              <div className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-4" data-testid="opt-economics-panel">
                <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Matched-quality economics</div>
                {economics ? (
                  <>
                    <div className="text-sm" data-testid="opt-economics-status">status: <span className={economics.status === "measured" ? "text-emerald-500" : "text-amber-500"}>{economics.status}</span></div>
                    {economics.reason && <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">reason: {economics.reason}</div>}
                    {economics.status === "measured" && (
                      <div className="text-xs mt-2 font-mono">baseline ${economics.baseline_cost} · selected ${economics.selected_cost} · savings ${economics.savings} ({(economics.savings_pct || 0).toFixed(2)}%)</div>
                    )}
                  </>
                ) : <div className="text-xs text-[var(--qn-foreground-subtle)]">Economics evidence unavailable.</div>}
                {(comparison || matchedLink) && (
                  <div className="mt-3 border-t border-[var(--qn-border)] pt-2" data-testid="opt-matched-comparison">
                    <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Matched comparison</div>
                    {matchedLink && (
                      <div className="text-xs font-mono">this run: {matchedLink.role} · counterpart: {matchedLink.counterpart_run_id}</div>
                    )}
                    {comparison && (
                      <div className="text-xs font-mono text-[var(--qn-foreground-muted)]">status: {(comparison.economics || {}).status} · baseline {comparison.baseline_run_id} · selected {comparison.selected_run_id}</div>
                    )}
                  </div>
                )}
                {limitations.length > 0 && (
                  <div className="mt-3" data-testid="opt-limitations">
                    <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)] mb-1">Declared limitations</div>
                    {limitations.map((l, i) => (<div key={i} className="text-xs font-mono text-amber-500">{l}</div>))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
